/* Keep Shopify's native plans/disclosures; only adapt the configured product price. */
(() => {
  if (customElements.get('coverly-installments')) return;

  class CoverlyInstallments extends HTMLElement {
    static parseMoney(text, locale = 'en-US') {
      if (typeof text !== 'string' || !text.trim() || /[-−–—%]/u.test(text)) return null;
      text = text.trim().replace(/^[A-Z]{2,3}\s*|\s*[A-Z]{3}$/g, '');
      if (!/^[\p{Sc}\d.,'’\s]+$/u.test(text)) return null;
      const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
      const group = parts.find(part => part.type === 'group')?.value;
      const decimal = parts.find(part => part.type === 'decimal')?.value || '.';
      let value = text.replace(/\s/gu, '');
      if (group) value = value.split(group).join('');
      value = value.replace(decimal, '.').replace(/[^\d.]/g, '');
      if (!/^\d+(?:\.\d+)?$/.test(value)) return null;
      const amount = Number(value);
      return Number.isFinite(amount) && amount >= 0 ? amount : null;
    }

    static metadataForPrice(meta, variantId, amount) {
      if (meta?.type !== 'product' || !Array.isArray(meta.financing_plans) ||
          !Array.isArray(meta.variants) || !Number.isFinite(amount) || amount < 0) return null;
      const variant = meta.variants.find(item => String(item.id) === String(variantId));
      if (!variant) return null;
      const plan = meta.financing_plans.find(item => {
        const min = this.parseMoney(item.min_price);
        const max = this.parseMoney(item.max_price);
        return min !== null && max !== null && amount >= min && amount <= max;
      });
      const splitTerms = (plan?.terms || []).filter(term =>
        term.loan_type === 'split_pay' && term.apr === 0 && term.installments_count > 0);
      const count = splitTerms.find(term => term.installments_count === variant.number_of_payment_terms)
        ?.installments_count || Math.max(0, ...splitTerms.map(term => term.installments_count));
      const money = new Intl.NumberFormat('en-US', {
        style: 'currency', currency: meta.currency_code
      });
      const updated = {
        ...variant,
        full_price: money.format(amount),
        eligible: Boolean(plan && variant.available),
        ...(count ? {
          number_of_payment_terms: count,
          price_per_term: money.format(amount / count)
        } : {})
      };
      return { ...meta, variants: meta.variants.map(item => item === variant ? updated : item) };
    }

    connectedCallback() {
      this.scope = document.getElementById(`MainProduct-${this.dataset.sectionId}`) ||
        this.closest('.shopify-section');
      if (!this.scope) return;
      this.schedule = () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.update(), 100);
      };
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target) ||
            (record.target === this.terms && record.type === 'attributes' &&
              (record.attributeName !== 'shopify-meta' ||
                this.terms.getAttribute('shopify-meta') !== this.writtenMeta)))) this.schedule();
      });
      this.observer.observe(this.scope, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['value', 'variant-id', 'shopify-meta']
      });
      this.scope.addEventListener('change', this.schedule);
      this.scope.addEventListener('input', this.schedule);
      this.update();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.scope?.removeEventListener('change', this.schedule);
      this.scope?.removeEventListener('input', this.schedule);
    }

    update() {
      const terms = this.querySelector('shopify-payment-terms');
      if (!terms) return;
      try {
        const raw = terms.getAttribute('shopify-meta');
        if (this.terms !== terms || raw !== this.writtenMeta) this.baseMeta = JSON.parse(raw);
        this.terms = terms;
        const ppo = this.scope.querySelector('#coverly-configurator, #ppo-configurator');
        if (!ppo) {
          this.hidden = false;
          if (this.writtenMeta && raw === this.writtenMeta) {
            terms.setAttribute('shopify-meta', JSON.stringify(this.baseMeta));
            this.writtenMeta = null;
          }
          return;
        }
        // Read just the payable text, excluding compare-at prices and savings. PPO
        // renders this again whenever options change, so observe its container.
        const price = ppo.querySelector('.ppo-price');
        const copy = price?.cloneNode(true);
        copy?.querySelectorAll('s, del, .ppo-price__was, .ppo-price__save, .ppo-price__from')
          .forEach(node => node.remove());
        const amount = this.constructor.parseMoney(copy?.textContent, navigator.language);
        const variantId = this.closest('form')?.querySelector('[name="id"]')?.value ||
          terms.getAttribute('variant-id');
        const currencyMatches = !ppo.dataset.currency ||
          ppo.dataset.currency === this.baseMeta?.currency_code;
        const meta = amount === null || !currencyMatches ? null :
          this.constructor.metadataForPrice(this.baseMeta, variantId, amount);
        // Don't leave a base-price quote visible while PPO is loading or its price
        // is unavailable. Unknown Shopify metadata also fails closed.
        this.hidden = !meta;
        if (!meta) return;
        const next = JSON.stringify(meta);
        this.writtenMeta = next;
        if (raw !== next) terms.setAttribute('shopify-meta', next);
        if (terms.getAttribute('variant-id') !== String(variantId)) {
          terms.setAttribute('variant-id', variantId);
        }
      } catch {
        this.hidden = true;
      }
    }
  }
  customElements.define('coverly-installments', CoverlyInstallments);
})();
